import React, { memo, useCallback, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import messages from './messages';
import type { Props } from './types';

export const RemoveButton = memo<Props>(
	({ issueKey, globalId, isRemote, shouldShowRemoveAction, onDelete }: Props) => {
		const intl = useIntl();

		const deleteButtonAction = useCallback(
			(e: SyntheticEvent<HTMLElement>) => {
				e.stopPropagation();
				if (issueKey != null) {
					onDelete(issueKey, globalId);
				}
			},
			[globalId, issueKey, onDelete],
		);

		const deleteButtonTooltipContent = shouldShowRemoveAction
			? intl.formatMessage(
					isRemote ? messages.remoteLinkDeleteLinkTooltip : messages.issueLinkDeleteLinkTooltip,
				)
			: '';

		/* A container surrounding the button is needed as tabbing from issue line card without an
		 *  intermediary always 'existent' container results in tabbing to 'nothing'/the body div
		 *  as the tab event occurs before the close button is chosen as the next node to tab into
		 */
		return (
			<Tooltip content={deleteButtonTooltipContent} position="bottom">
				<DeleteLinkContainer
					show={shouldShowRemoveAction}
					data-testid="issue-line-card.ui.remove-button.delete-link-container"
				>
					<Button
						testId="issue-line-card.ui.remove-button"
						iconBefore={<EditorCloseIcon label={deleteButtonTooltipContent} />}
						onClick={deleteButtonAction}
						appearance="subtle"
						spacing="none"
					/>
				</DeleteLinkContainer>
			</Tooltip>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DeleteLinkContainer = styled.div<{ show?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	height: '100%',
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize / 4}px``
	marginLeft: token('space.025', '2px'),
	marginRight: token('space.negative.100', '-8px'),
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.show ? 1 : 0),
});
